import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import axios, { HttpStatusCode } from "axios";

import {
  MAPBOX_BASE_API_URL,
  MAPBOX_V5_API_URL,
  NP_API_URL,
  NP_CALCULATOR_API_URL
} from "../config/constants";
import { errorView } from "../helpers/ErrorHandler";
import { getTokenInForCompanyLocal } from "../utils/cacheStorage";
import { getValidAccessToken, handleInvalidTokens, refreshAccessToken } from "./tokenService";

type Nullable<T> = T | null;

export interface ApiServiceErr {
  msg: string;
  status: HttpStatusCode;
  errors?: string[];
}

export interface DeleteBody {
  id: string;
}

export type MutOptions<Response, TVariables = unknown> = UseMutationOptions<
  Response,
  ApiServiceErr,
  TVariables,
  unknown
>;

export type QueryOptions<Response, TVariables = unknown> = UseQueryOptions<
  Response,
  ApiServiceErr,
  TVariables,
  any[]
>;

export type QueryOpt<Response> = UseQueryOptions<Response, ApiServiceErr, any, any>;

const getApiError = (error: any, defaultMessage = "Something went wrong") => {
  if (typeof error === "string") return error;
  if (error?.msg) return error.msg;

  const data = error?.response?.data;

  if (typeof data === "string") return data;
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.error === "string") return data.error;

  return defaultMessage;
};

const customConfig = {
  headers: {
    authorization: getTokenInForCompanyLocal()
  }
};

const axiosApi = axios.create({ baseURL: NP_API_URL });

const calculatorApi = axios.create({ baseURL: NP_CALCULATOR_API_URL });

const axiosAuth = axios.create({ baseURL: NP_API_URL });

const axiosMapBox = axios.create({ baseURL: MAPBOX_BASE_API_URL });

const axiosMapBoxV5 = axios.create({ baseURL: MAPBOX_V5_API_URL });

// Response Interceptor (401 handling)
const errorInterceptor = async (instance: any, error: any) => {
  const originalRequest = error.config;

  if (!originalRequest) {
    return Promise.reject(error);
  }

  if (error?.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;
    try {
      const newToken = await refreshAccessToken();
      if (!newToken) {
        handleInvalidTokens();
        return Promise.reject(error);
      }

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return instance(originalRequest);
    } catch (refreshError) {
      handleInvalidTokens();
      return Promise.reject(refreshError);
    }
  }
  const message = getApiError(error);
  errorView(message, { force: true });
  return Promise.reject(error);
};

const beforeRequestIntercept = async (config: any) => {
  const token: any = await getValidAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

export const applyInterceptors = (axiosInstance: any) => {
  axiosInstance.interceptors.request.use(beforeRequestIntercept, Promise.reject);
  axiosInstance.interceptors.response.use(
    (res: any) => {
      return res;
    },
    (err: any) => errorInterceptor(axiosInstance, err)
  );
};

applyInterceptors(axiosApi);

export {
  axiosApi,
  axiosAuth,
  axiosMapBox,
  axiosMapBoxV5,
  calculatorApi,
  customConfig,
  getApiError
};
